import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { StatusBadge } from "@/components/Bits";
import api, { COUNTRY_NAMES, fmt, fmtTime } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Copy, Pencil, Undo2 } from "lucide-react";

export default function Campaigns() {
  const [items, setItems] = useState([]);
  const navigate = useNavigate();

  const load = async () => setItems((await api.get("/campaigns")).data);
  useEffect(() => { load(); }, []);

  const remove = async (c) => {
    try {
      await api.delete(`/campaigns/${c.id}`);
      toast.success("Campagne supprimée");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const duplicate = async (c) => {
    try {
      const { data } = await api.post(`/campaigns/${c.id}/duplicate`);
      toast.success("Copie créée en brouillon");
      navigate(`/campagnes/${data.id}/modifier`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Duplication impossible");
    }
  };

  const reopen = async (c) => {
    try {
      await api.post(`/campaigns/${c.id}/reopen`);
      toast.success("Campagne remise en brouillon");
      navigate(`/campagnes/${c.id}/modifier`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Impossible de rouvrir");
    }
  };

  return (
    <Layout
      title="Campagnes"
      subtitle="Diffusions promotionnelles multi-instances"
      actions={
        <button onClick={() => navigate("/campagnes/nouvelle")} data-testid="new-campaign-btn"
          className="rounded-lg bg-emerald-500 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-600 hover:-translate-y-[1px] transition-transform duration-200 flex items-center gap-2">
          <Plus className="h-4 w-4" /> Nouvelle campagne
        </button>
      }
    >
      <div className="card-tactical overflow-hidden" data-testid="campaigns-page">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
            <tr>
              <th className="px-4 py-3">Campagne</th>
              <th className="px-4 py-3">Cibles</th>
              <th className="px-4 py-3">Statut</th>
              <th className="px-4 py-3">File</th>
              <th className="px-4 py-3">Envoyés</th>
              <th className="px-4 py-3">Livrés</th>
              <th className="px-4 py-3">Lus</th>
              <th className="px-4 py-3">Échecs</th>
              <th className="px-4 py-3">Planifiée / créée</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="campaigns-table-body">
            {items.map((c) => (
              <tr key={c.id} className="border-t border-gray-100 hover:bg-gray-50 transition-colors duration-150 cursor-pointer"
                onClick={() => navigate(`/campagnes/${c.id}`)} data-testid={`campaign-row-${c.id}`}>
                <td className="px-4 py-3 font-medium text-gray-900">{c.name}</td>
                <td className="px-4 py-3 text-xs text-gray-500">
                  {(c.target_countries || []).map((x) => COUNTRY_NAMES[x] || x).join(", ") || "Tous"}
                  {c.target_tags?.length ? ` · ${c.target_tags.join(", ")}` : ""}
                </td>
                <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
                <td className="px-4 py-3 mono">{fmt(c.stats?.queued)}</td>
                <td className="px-4 py-3 mono text-emerald-700">{fmt(c.stats?.sent)}</td>
                <td className="px-4 py-3 mono text-emerald-600">{fmt(c.stats?.delivered)}</td>
                <td className="px-4 py-3 mono text-sky-700">{fmt(c.stats?.read)}</td>
                <td className="px-4 py-3 mono text-red-600">{fmt(c.stats?.failed)}</td>
                <td className="px-4 py-3 mono text-xs text-gray-500">
                  {c.scheduled_for ? fmtTime(c.scheduled_for) : fmtTime(c.created_at)}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                    {["draft", "scheduled"].includes(c.status) ? (
                      <button onClick={(e) => { e.stopPropagation(); navigate(`/campagnes/${c.id}/modifier`); }}
                        data-testid={`edit-campaign-${c.id}`} title="Modifier"
                        className="rounded-md border border-gray-200 p-1.5 hover:bg-gray-100">
                        <Pencil className="h-3.5 w-3.5 text-gray-600" />
                      </button>
                    ) : c.status !== "running" ? (
                      <button onClick={(e) => { e.stopPropagation(); reopen(c); }}
                        data-testid={`reopen-campaign-${c.id}`} title="Remettre en brouillon"
                        className="rounded-md border border-gray-200 p-1.5 hover:bg-gray-100">
                        <Undo2 className="h-3.5 w-3.5 text-gray-600" />
                      </button>
                    ) : null}
                    <button onClick={(e) => { e.stopPropagation(); duplicate(c); }}
                      data-testid={`duplicate-campaign-${c.id}`} title="Dupliquer"
                      className="rounded-md border border-gray-200 p-1.5 hover:bg-gray-100">
                      <Copy className="h-3.5 w-3.5 text-gray-600" />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); remove(c); }} data-testid={`delete-campaign-${c.id}`}
                      title="Supprimer" className="rounded-md border border-gray-200 p-1.5 hover:bg-red-50">
                      <Trash2 className="h-3.5 w-3.5 text-red-500" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan="10" className="px-4 py-10 text-center text-sm text-gray-500">Aucune campagne.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Layout>
  );
}
